import { useCallback, useEffect, useRef, useState } from "react";
import { SECTION_IDS } from "../utils/invoice-steps";

export function useInvoiceScrollSpy() {
  const [activeStep, setActiveStep] = useState(0);
  const lockUntilRef = useRef(0);

  const goToStep = useCallback((index: number) => {
    if (index < 0 || index >= SECTION_IDS.length) return;
    setActiveStep(index);
    // Pin the clicked step for long enough that smooth-scroll can finish
    // without the observer/scroll handler overriding mid-flight.
    lockUntilRef.current = Date.now() + 900;
    const el = document.getElementById(SECTION_IDS[index]);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleScroll = useCallback(() => {
    if (Date.now() < lockUntilRef.current) return;
    // When the page is scrolled to the bottom, scrollIntoView can't bring the
    // last section's top to y=150, so fall back to forcing the last step active.
    const scrollY = window.scrollY;
    const viewportH = window.innerHeight;
    const pageH = document.documentElement.scrollHeight;
    if (pageH - (scrollY + viewportH) < 24) {
      setActiveStep(SECTION_IDS.length - 1);
      return;
    }
    const offsets = SECTION_IDS.map((id) => {
      const el = document.getElementById(id);
      if (!el) return Infinity;
      return Math.abs(el.getBoundingClientRect().top - 150);
    });
    const closest = offsets.indexOf(Math.min(...offsets));
    if (closest >= 0) setActiveStep(closest);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < lockUntilRef.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const i = SECTION_IDS.indexOf(entry.target.id as (typeof SECTION_IDS)[number]);
            if (i >= 0) setActiveStep(i);
          }
        });
      },
      { threshold: 0.3, rootMargin: "-150px 0px -50% 0px" },
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, [handleScroll]);

  return { activeStep, goToStep };
}
